import { QueryCache, QueryClient, QueryClientProvider, MutationCache } from '@tanstack/react-query'
import React from 'react'
import ReactDOM from 'react-dom/client'
import { Toaster } from 'react-hot-toast'
import axios from 'axios'
import * as Sentry from '@sentry/react'
import './index.css'
import Router from './router'; // Import the Router component
import { initSentry, captureApiError } from '@/utils/sentry'
import { initGA } from '@/utils/analytics'

// Initialize Sentry (must be first to catch init errors)
initSentry();

// Initialize Google Analytics 4
initGA();

// One-time cleanup: unregister the legacy root-scoped spline-sw.js for
// returning users. It used to claim "/" and block OneSignalSDKWorker.
// spline-sw is now registered narrowly from auth pages (/login, /register,
// /forgot-password); those scopes must be left alone here.
if ('serviceWorker' in navigator) {
  navigator.serviceWorker
    .getRegistrations()
    .then((regs) => {
      regs.forEach((r) => {
        const isSpline = r.active?.scriptURL?.endsWith('/spline-sw.js');
        const isRootScope = r.scope ? new URL(r.scope).pathname === '/' : false;
        if (isSpline && isRootScope) {
          r.unregister()
            .then((ok) => {
              if (ok) {
                console.info('[SW Cleanup] Unregistered legacy root-scoped spline-sw.js');
              } else {
                console.warn('[SW Cleanup] spline-sw.js unregister returned false');
              }
            })
            .catch((err) => {
              console.warn('[SW Cleanup] spline-sw.js unregister failed:', err);
            });
        }
      });
    })
    .catch((err) => {
      console.warn('[SW Cleanup] getRegistrations() failed:', err);
    });
}

// Forward every React Query / mutation failure to Sentry. AxiosErrors go
// through captureApiError for structured fingerprinting; anything else is
// captured as a plain exception. Per-hook onError handlers still run — this
// is a safety net, not a replacement.
const reportQueryError = (error: unknown): void => {
  if (axios.isAxiosError(error)) {
    captureApiError(error);
    return;
  }
  if (error instanceof Error) {
    Sentry.captureException(error);
  }
};

const queryClient = new QueryClient({
  queryCache: new QueryCache({ onError: reportQueryError }),
  mutationCache: new MutationCache({ onError: reportQueryError }),
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
})

ReactDOM.createRoot(document.getElementById('root')!).render(
  <QueryClientProvider client={queryClient}>
    <Router />
    <Toaster
      position="top-right"
      toastOptions={{
        duration: 4000,
        style: {
          background: '#363636',
          color: '#fff',
        },
        success: {
          style: {
            background: '#22c55e',
          },
        },
        error: {
          style: {
            background: '#ef4444',
          },
        },
      }}
    />
  </QueryClientProvider>
)
